import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {
  getContractConfig,
  saveContractConfig,
  DEFAULT_CONTRACT_CONFIG,
} from "../../../services/backend/ContractConfigService";
import PaymentCutoffConfig from "./PaymentCutoffConfig";

// Administración de los catálogos del módulo de Contratos (config/contracts):
// dependencias, naturalezas (por grupo default/DEX), modalidades de
// contratación con sus tipos dependientes y las fechas de corte de las actas
// (paymentCutoff, pestaña propia). Solo ADMIN (mismo gate de
// ApplicantConfig/PqrsdConfig). Los valores se desactivan, nunca se borran:
// los contratos y perfiles existentes guardan el texto (label) y deben seguir
// resolviéndolo.

const slugify = (s) =>
  String(s || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const sorted = (list) => (list || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

// Reordena intercambiando el campo `order` con el vecino (dirección -1 sube, +1 baja).
const swapOrder = (list, key, dir) => {
  const arr = sorted(list);
  const idx = arr.findIndex((it) => it.key === key);
  const other = idx + dir;
  if (idx < 0 || other < 0 || other >= arr.length) return list;
  const a = arr[idx];
  const b = arr[other];
  return list.map((it) => {
    if (it.key === a.key) return { ...it, order: b.order ?? 0 };
    if (it.key === b.key) return { ...it, order: a.order ?? 0 };
    return it;
  });
};

// Aplica alta o edición (por key original) garantizando unicidad de key.
const upsertItem = (list, item, origKey) => {
  if (origKey) return (list || []).map((it) => (it.key === origKey ? { ...it, ...item } : it));
  const maxOrder = Math.max(0, ...(list || []).map((it) => it.order || 0));
  return [...(list || []), { ...item, enabled: true, order: maxOrder + 10 }];
};

// Editor genérico de una lista de catálogo {key, label, enabled, order}.
function CatalogList({ title, items, onChange, onEdit, extraActions }) {
  const list = sorted(items);
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => onEdit(null)}>
            Agregar
          </Button>
        </Stack>
        {list.length === 0 && (
          <Typography variant="body2" color="text.secondary">Sin valores configurados.</Typography>
        )}
        {list.map((it) => (
          <Stack key={it.key} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="body2" sx={{ flex: 1, opacity: it.enabled === false ? 0.5 : 1 }}>
              {it.label}
              {it.enabled === false && <Chip label="Inactivo" size="small" sx={{ ml: 1 }} />}
            </Typography>
            {extraActions && extraActions(it)}
            <Tooltip title="Subir">
              <span>
                <IconButton size="small" onClick={() => onChange(swapOrder(items, it.key, -1))}>
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Bajar">
              <span>
                <IconButton size="small" onClick={() => onChange(swapOrder(items, it.key, 1))}>
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={it.enabled === false ? "Activar" : "Desactivar"}>
              <Switch
                size="small"
                checked={it.enabled !== false}
                onChange={(e) => onChange(items.map((x) => (x.key === it.key ? { ...x, enabled: e.target.checked } : x)))}
              />
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEdit(it)}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ContractConfig() {
  const user = useSelector((state) => state.user.value);
  const navigate = useNavigate();
  const isAdmin = !!(user?.roles || []).includes("ADMIN");

  const [cfg, setCfg] = useState(DEFAULT_CONTRACT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [tab, setTab] = useState("dependencies");
  // { listPath: 'dependencies' | 'natureGroups.default' | 'natureGroups.dex' |
  //   'modalities' | {modalityKey}, origKey, label }
  const [dialog, setDialog] = useState(null);
  const [expandedModality, setExpandedModality] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/");
      return;
    }
    (async () => {
      setCfg(await getContractConfig());
      setLoading(false);
    })();
  }, [isAdmin, navigate]);

  const persist = async (patch) => {
    setSaving(true);
    const ok = await saveContractConfig(patch);
    setSaving(false);
    setFeedback(ok
      ? { severity: "success", msg: "Configuración guardada." }
      : { severity: "error", msg: "No fue posible guardar la configuración." });
    return ok;
  };

  const saveDependencies = (next) => {
    setCfg((c) => ({ ...c, dependencies: next }));
    return persist({ dependencies: next });
  };

  const saveNatureGroup = (groupKey, next) => {
    const natureGroups = { ...cfg.natureGroups, [groupKey]: next };
    setCfg((c) => ({ ...c, natureGroups }));
    return persist({ natureGroups });
  };

  const saveModalities = (next) => {
    setCfg((c) => ({ ...c, modalities: next }));
    return persist({ modalities: next });
  };

  // Fechas de corte (payment-monthly-cutoff): el componente entrega la
  // configuración completa ya editada por los helpers puros de paymentCutoff.js.
  const savePaymentCutoff = (next) => {
    setCfg((c) => ({ ...c, paymentCutoff: next }));
    return persist({ paymentCutoff: next });
  };

  const saveModalityTypes = (modalityKey, nextTypes) => {
    const next = (cfg.modalities || []).map((m) => (m.key === modalityKey ? { ...m, types: nextTypes } : m));
    return saveModalities(next);
  };

  // Resuelve la lista y su función de guardado a partir del path del diálogo.
  const listByPath = (path) => {
    if (path === "dependencies") return { list: cfg.dependencies, save: saveDependencies };
    if (path === "natureGroups.default") return { list: cfg.natureGroups?.default, save: (n) => saveNatureGroup("default", n) };
    if (path === "natureGroups.dex") return { list: cfg.natureGroups?.dex, save: (n) => saveNatureGroup("dex", n) };
    if (path === "modalities") return { list: cfg.modalities, save: saveModalities };
    // path = { modalityKey } → tipos de esa modalidad
    const modality = (cfg.modalities || []).find((m) => m.key === path.modalityKey);
    return { list: modality?.types || [], save: (n) => saveModalityTypes(path.modalityKey, n) };
  };

  const openDialog = (listPath, item) =>
    setDialog({ listPath, origKey: item?.key || null, origLabel: item?.label || "", label: item?.label || "" });

  const handleDialogSave = async () => {
    const label = (dialog.label || "").trim();
    if (!label) {
      setFeedback({ severity: "warning", msg: "El nombre es obligatorio." });
      return;
    }
    const { list, save } = listByPath(dialog.listPath);
    if ((list || []).some((it) => it.label === label && it.key !== dialog.origKey)) {
      setFeedback({ severity: "warning", msg: "Ya existe un valor con ese nombre." });
      return;
    }
    let item;
    if (dialog.origKey) {
      item = { label };
    } else {
      // Garantiza unicidad de key ante labels que colisionen al normalizar.
      const base = slugify(label);
      const taken = new Set((list || []).map((it) => it.key));
      let key = base;
      for (let i = 1; taken.has(key); i += 1) key = `${base}-${i}`;
      item = { key, label };
    }
    const ok = await save(upsertItem(list, item, dialog.origKey));
    if (ok) setDialog(null);
  };

  if (!isAdmin) return null;

  const renaming = dialog?.origKey && dialog.label.trim() !== dialog.origLabel;

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Configuración de contratos</Typography>
      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {/* Pendiente: pestaña para config/contracts.paymentRequestDocs, el
            catálogo documental de las solicitudes de pago jurídicas (C5). Hoy
            ese catálogo opera con su semilla (DEFAULT_PAYMENT_REQUEST_DOCS) y
            solo puede cambiarse escribiendo el documento a mano; agregar la
            pestaña exige editar required/source/entityDocKey/maxBytes por
            entrada, que es trabajo propio y no entró en el alcance de C5. */}
        <Tab value="dependencies" label="Dependencias" />
        <Tab value="natures" label="Naturalezas" />
        <Tab value="modalities" label="Modalidades y tipos" />
        <Tab value="cutoff" label="Fechas de corte" />
      </Tabs>

      {loading ? (
        <Typography variant="body2" color="text.secondary">Cargando…</Typography>
      ) : (
        <>
          {tab === "dependencies" && (
            <CatalogList
              title="Dependencias del contrato"
              items={cfg.dependencies}
              onChange={saveDependencies}
              onEdit={(it) => openDialog("dependencies", it)}
            />
          )}

          {tab === "natures" && (
            <>
              <CatalogList
                title="Naturalezas estándar (contratos CPSDF / RCPS)"
                items={cfg.natureGroups?.default}
                onChange={(n) => saveNatureGroup("default", n)}
                onEdit={(it) => openDialog("natureGroups.default", it)}
              />
              <CatalogList
                title="Convenios Dirección de Extensión (contratos DEX)"
                items={cfg.natureGroups?.dex}
                onChange={(n) => saveNatureGroup("dex", n)}
                onEdit={(it) => openDialog("natureGroups.dex", it)}
              />
              <Alert severity="info">
                Las naturalezas también alimentan el acceso por naturaleza de los usuarios financieros
                (naturalezas permitidas) y los filtros del módulo de financiación.
              </Alert>
            </>
          )}

          {tab === "modalities" && (
            <CatalogList
              title="Modalidades de contratación"
              items={cfg.modalities}
              onChange={saveModalities}
              onEdit={(it) => openDialog("modalities", it)}
              extraActions={(it) => (
                <Tooltip title="Tipos de contrato de la modalidad">
                  <IconButton
                    size="small"
                    onClick={() => setExpandedModality(expandedModality === it.key ? null : it.key)}
                  >
                    {expandedModality === it.key ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                  </IconButton>
                </Tooltip>
              )}
            />
          )}

          {tab === "cutoff" && (
            <PaymentCutoffConfig
              value={cfg.paymentCutoff}
              natureGroups={cfg.natureGroups}
              saving={saving}
              onSave={savePaymentCutoff}
            />
          )}

          {tab === "modalities" && expandedModality && (
            <Box sx={{ pl: 3 }}>
              <CatalogList
                title={`Tipos de contrato — ${(cfg.modalities || []).find((m) => m.key === expandedModality)?.label || ""}`}
                items={(cfg.modalities || []).find((m) => m.key === expandedModality)?.types || []}
                onChange={(n) => saveModalityTypes(expandedModality, n)}
                onEdit={(it) => openDialog({ modalityKey: expandedModality }, it)}
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dialog?.origKey ? "Editar valor" : "Agregar valor"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre"
            value={dialog?.label || ""}
            onChange={(e) => setDialog((d) => ({ ...d, label: e.target.value }))}
            sx={{ mt: 1 }}
          />
          {renaming && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Los contratos y perfiles existentes conservan el texto anterior
              ("{dialog.origLabel}"). Renombrar solo afecta contratos nuevos; si el valor
              cambió de significado, desactívalo y crea uno nuevo.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={handleDialogSave}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
